import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import AgenticTopBar from '../TopBar/AgenticTopBar';
import AgenticSidebar from '../Sidebar/AgenticSidebar';
import AgenticLandingContent from '../LandingPage/AgenticLandingContent';
import {
    AgenticMainLayoutProps,
    DEFAULT_BREADCRUMBS,
    DEFAULT_QUICK_ACTIONS,
    QuickAction,
} from '../AskAsperAgentic.types';
import {
    createMixpanelEventFirer,
    getTextLength,
    LANDING_PAGE_VIEW,
    LANDING_PAGE_QUESTION_BANK_CLICK,
    LANDING_PAGE_PROMPT_PILL_CLICK,
    LANDING_PAGE_SAVED_QUESTIONS_CLICK,
    LANDING_PAGE_SEND_CLICK,
    LANDING_PAGE_THREAD_PANEL_CLICK,
    LANDING_PAGE_INPUT_FOCUS,
    QUESTION_BANK_VIEW,
    QUESTION_BANK_SAVED_QUESTION_EDIT,
    QUESTION_BANK_SAVED_QUESTION_DELETE,
    QUESTION_BANK_SEARCH,
    QUESTION_BANK_SEARCH_CLOSE,
    THREAD_PANEL_VIEW,
    THREAD_PANEL_THREAD_CLICK,
    THREAD_PANEL_THREAD_EDIT,
    THREAD_PANEL_THREAD_DELETE,
    THREAD_PANEL_SEARCH,
    THREAD_PANEL_SEARCH_CLOSE,
    THREAD_PANEL_NEW_CHAT_CLICK,
} from '../utils/mixpanelEvents';

/**
 * AgenticMainLayout - Main container layout combining TopBar, Sidebar, and Content
 * Part of the AskAsper Agentic Platform components
 *
 * This component provides a complete landing page layout with:
 * - Top navigation bar with breadcrumbs (Menu icon toggles sidebar drawer)
 * - Collapsible sidebar drawer (opens from the side via Menu icon, flex layout)
 * - Gradient background effect
 * - Center content area (landing or custom content via children)
 */
const AgenticMainLayout: React.FC<AgenticMainLayoutProps> = ({
    // Layout configuration
    showTopBar = true,
    disableNavbar = false,
    footerHeight = 0,

    // User info
    userName = 'User',

    // Sidebar - threads and related
    threads = [],
    threadsLoading = false,
    activeThreadId,
    user: sidebarUser,
    sidebarDefaultCollapsed = false,
    sidebarHideHeader = false,

    // Breadcrumb
    breadcrumbs = DEFAULT_BREADCRUMBS,

    // Quick actions
    quickActions = DEFAULT_QUICK_ACTIONS,

    // Search state
    searchValue: controlledSearchValue,
    isLoading = false,

    // Event handlers
    onNewChat,
    onHistoryClick,
    onNotificationClick,
    onBreadcrumbClick,
    onHomeClick,
    onSearchChange,
    onSearchSubmit,
    onQuickActionClick,
    onQuestionBankClick,
    onSaveAsCustom,
    questionBankCategories,
    customQuestions,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
    onExportClick,
    onThreadSelect,
    onThreadEdit,
    onThreadDelete,
    onUserClick,

    // Mixpanel Analytics
    onTrackEvent,

    // Children for custom content
    children,

    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // ===== Mixpanel: safe fire helper (no-ops when onTrackEvent is not provided) =====
    const fireEvent = useMemo(() => createMixpanelEventFirer(onTrackEvent), [onTrackEvent]);

    // Internal state for uncontrolled mode
    const [internalSearchValue, setInternalSearchValue] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Use controlled or uncontrolled values
    const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;

    // Mixpanel: track the most recent prompt-pill text so we can mark a Send as source: 'prompt'
    // when the user submits without editing. Cleared when the user edits the input.
    const lastPromptPillTextRef = useRef<string | undefined>(undefined);

    // Mixpanel: Fire landing page view event on mount (only when the layout actually renders the landing
    // content — when `children` is provided the host owns the viewport, so we skip the auto-fire).
    useEffect(() => {
        if (children == null) {
            fireEvent(LANDING_PAGE_VIEW);
        }
        // Fire only once per mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handlers
    const handleMenuToggle = useCallback(() => {
        setSidebarOpen((prev) => {
            const nextOpen = !prev;
            if (nextOpen) {
                // Sequence per spec: pill click on landing page → thread panel opens
                fireEvent(LANDING_PAGE_THREAD_PANEL_CLICK);
                fireEvent(THREAD_PANEL_VIEW);
            }
            return nextOpen;
        });
    }, [fireEvent]);

    const handleSidebarClose = useCallback(() => {
        setSidebarOpen(false);
    }, []);

    // Close sidebar on Escape
    useEffect(() => {
        if (!sidebarOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleSidebarClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [sidebarOpen, handleSidebarClose]);

    const handleSearchChange = useCallback(
        (value: string) => {
            // Clear last-prompt-pill marker when the user edits the input away from the pill text
            if (lastPromptPillTextRef.current && lastPromptPillTextRef.current !== value) {
                lastPromptPillTextRef.current = undefined;
            }
            if (onSearchChange) {
                onSearchChange(value);
            } else {
                setInternalSearchValue(value);
            }
        },
        [onSearchChange],
    );

    // ===== Mixpanel: wrapped landing-content handlers =====
    const handleQuickActionClick = useCallback(
        (action: QuickAction) => {
            const mappedQuestion = action.fullQuestion ?? action.label;
            fireEvent(LANDING_PAGE_PROMPT_PILL_CLICK, {
                prompt_pill_id: action.id,
                prompt_pill_label: action.label,
                mapped_question: mappedQuestion,
            });
            lastPromptPillTextRef.current = mappedQuestion;
            onQuickActionClick?.(action);
        },
        [onQuickActionClick, fireEvent],
    );

    const handleSearchSubmit = useCallback(
        (value: string) => {
            const source = lastPromptPillTextRef.current && lastPromptPillTextRef.current === value ? 'prompt' : 'typed';
            // No thread_id on the landing page (this is the first send); host wires thread later.
            fireEvent(LANDING_PAGE_SEND_CLICK, {
                source,
                question_text_length: getTextLength(value),
            });
            lastPromptPillTextRef.current = undefined;
            onSearchSubmit?.(value);
        },
        [onSearchSubmit, fireEvent],
    );

    const handleQuestionBankClick = useCallback(() => {
        // Sequence per spec: landing-page click → question-bank modal view
        fireEvent(LANDING_PAGE_QUESTION_BANK_CLICK);
        fireEvent(QUESTION_BANK_VIEW);
        onQuestionBankClick?.();
    }, [onQuestionBankClick, fireEvent]);

    // Mixpanel: fire only once per "focus session" so rapid blur/focus does not spam
    const hasFiredInputFocusRef = useRef(false);
    const handleSearchInputFocus = useCallback(() => {
        if (hasFiredInputFocusRef.current) return;
        hasFiredInputFocusRef.current = true;
        fireEvent(LANDING_PAGE_INPUT_FOCUS);
    }, [fireEvent]);

    const handleSaveAsCustom = useCallback(
        (value: string) => {
            // "Save as custom" on the landing page is the user's saved-questions touchpoint here.
            fireEvent(LANDING_PAGE_SAVED_QUESTIONS_CLICK, {
                question_text_length: getTextLength(value),
            });
            onSaveAsCustom?.(value);
        },
        [onSaveAsCustom, fireEvent],
    );

    const handleEditCustomQuestion = useCallback(
        (question: { id?: string | number; text?: string; QuestionID?: number; Question?: string }) => {
            const questionId = question.id ?? question.QuestionID;
            const newText = question.text ?? question.Question ?? '';
            const oldText = customQuestions?.find((q) => String(q.id) === String(questionId))?.text;
            fireEvent(QUESTION_BANK_SAVED_QUESTION_EDIT, {
                question_id: questionId,
                old_question_title: oldText,
                new_question_title: newText,
            });
            onEditCustomQuestion?.(question);
        },
        [onEditCustomQuestion, customQuestions, fireEvent],
    );

    const handleDeleteCustomQuestion = useCallback(
        (questionId: string) => {
            const deletedTitle = customQuestions?.find((q) => q.id === questionId)?.text;
            fireEvent(QUESTION_BANK_SAVED_QUESTION_DELETE, {
                question_id: questionId,
                question_title: deletedTitle,
            });
            onDeleteCustomQuestion?.(questionId);
        },
        [onDeleteCustomQuestion, customQuestions, fireEvent],
    );

    // ===== Mixpanel: wrapped sidebar (thread panel) handlers =====
    const handleThreadSelect = useCallback(
        (threadId: string) => {
            const thread = threads.find((t) => t.thread_id === threadId);
            fireEvent(THREAD_PANEL_THREAD_CLICK, {
                thread_id: threadId,
                thread_title: thread?.thread_name,
            });
            onThreadSelect?.(threadId);
        },
        [onThreadSelect, threads, fireEvent],
    );

    const handleThreadEdit = useCallback(
        (threadId: string, newName: string) => {
            const oldName = threads.find((t) => t.thread_id === threadId)?.thread_name;
            fireEvent(THREAD_PANEL_THREAD_EDIT, {
                thread_id: threadId,
                old_thread_title: oldName,
                new_thread_title: newName,
            });
            onThreadEdit?.(threadId, newName);
        },
        [onThreadEdit, threads, fireEvent],
    );

    const handleThreadDelete = useCallback(
        (threadId: string) => {
            const thread = threads.find((t) => t.thread_id === threadId);
            fireEvent(THREAD_PANEL_THREAD_DELETE, {
                thread_id: threadId,
                thread_title: thread?.thread_name,
            });
            onThreadDelete?.(threadId);
        },
        [onThreadDelete, threads, fireEvent],
    );

    const handleSidebarSearch = useCallback(() => {
        // Spec: pass length only (privacy-first). The current sidebar's onSearchChats
        // doesn't pass the search text, so we fire with no payload here.
        fireEvent(THREAD_PANEL_SEARCH);
        onHistoryClick?.();
    }, [onHistoryClick, fireEvent]);

    const handleSidebarSearchClear = useCallback(() => {
        fireEvent(THREAD_PANEL_SEARCH_CLOSE);
    }, [fireEvent]);

    const handleQuestionBankSearch = useCallback(
        (value: string) => {
            fireEvent(QUESTION_BANK_SEARCH, { search_text_length: getTextLength(value) });
        },
        [fireEvent],
    );

    const handleQuestionBankSearchClear = useCallback(() => {
        fireEvent(QUESTION_BANK_SEARCH_CLOSE);
    }, [fireEvent]);

    const handleNewChat = useCallback(() => {
        fireEvent(THREAD_PANEL_NEW_CHAT_CLICK);
        onNewChat?.();
    }, [onNewChat, fireEvent]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white flex flex-col w-full overflow-hidden"
                style={
                    footerHeight != null && footerHeight > 0
                        ? { height: `calc(100vh - ${footerHeight}px)` }
                        : { height: '100vh' }
                }
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-main-layout
            >
                {/* Top Bar - hidden when disableNavbar (host app has its own navbar) */}
                {showTopBar && !disableNavbar && (
                    <AgenticTopBar
                        breadcrumbs={breadcrumbs}
                        onBreadcrumbClick={onBreadcrumbClick}
                        onHomeClick={onHomeClick}
                        onNotificationClick={onNotificationClick}
                        onMenuToggle={handleMenuToggle}
                        showCreateCaseButton={false}
                        showViewModeToggle={false}
                        onExportClick={onExportClick}
                        data-test-id={`${dataTestId}-top-bar`}
                    />
                )}

                {/* Main Content Area - flex layout with sidebar drawer */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar Drawer - opens from the side via Menu icon */}
                    <div
                        className="flex shrink-0 h-full overflow-hidden transition-[width] duration-200 ease-out"
                        style={{ width: sidebarOpen ? 244 : 0 }}
                    >
                        {sidebarOpen && (
                            <AgenticSidebar
                                threads={threads}
                                threadsLoading={threadsLoading}
                                activeThreadId={activeThreadId}
                                user={sidebarUser ?? { name: userName }}
                                defaultCollapsed={sidebarDefaultCollapsed}
                                hideHeader={sidebarHideHeader}
                                onNewChat={handleNewChat}
                                onSearchChats={handleSidebarSearch}
                                onSearchChatsClear={handleSidebarSearchClear}
                                onThreadSelect={handleThreadSelect}
                                onThreadEdit={handleThreadEdit}
                                onThreadDelete={handleThreadDelete}
                                onToggleSidebar={handleSidebarClose}
                                onUserClick={onUserClick}
                                data-test-id={`${dataTestId}-sidebar`}
                            />
                        )}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 min-w-0 relative overflow-auto">
                        {/* Background Gradient Effect */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                            {/* Top-right gradient blob */}
                            <div
                                className="absolute top-0 right-0 w-[600px] h-[500px] opacity-70"
                                style={{
                                    background:
                                        'radial-gradient(ellipse at center, rgba(167,139,250,0.8) 0%, rgba(147,197,253,0.5) 40%, transparent 70%)',
                                    filter: 'blur(60px)',
                                    transform: 'translate(10%, -10%)',
                                }}
                            />
                            {/* Bottom-left gradient blob */}
                            <div
                                className="absolute bottom-0 left-0 w-[500px] h-[400px] opacity-80"
                                style={{
                                    background:
                                        'radial-gradient(ellipse at center, rgba(167,139,250,0.5) 0%, rgba(196,181,253,0.4) 50%, transparent 70%)',
                                    filter: 'blur(60px)',
                                    transform: 'translate(-20%, 20%)',
                                }}
                            />

                            {/* Background Gradient Effect - Diagonal linear gradient from top-right (light purple) to bottom-left (white) */}
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        'linear-gradient(240deg, rgba(224,217,255,0.6) 0%, rgba(240,235,255,0.3) 40%, rgba(250,248,255,0.1) 60%, rgba(255,255,255,0) 100%)',
                                }}
                            />
                        </div>

                        {/* Content */}
                        <div className="relative z-10 h-full flex items-center justify-center p-4">
                            {children || (
                                <AgenticLandingContent
                                    userName={userName}
                                    quickActions={quickActions}
                                    searchValue={searchValue}
                                    onSearchChange={handleSearchChange}
                                    onSearchSubmit={handleSearchSubmit}
                                    onQuickActionClick={handleQuickActionClick}
                                    onQuestionBankClick={handleQuestionBankClick}
                                    onSaveAsCustom={handleSaveAsCustom}
                                    onSearchInputFocus={handleSearchInputFocus}
                                    onQuestionBankSearch={handleQuestionBankSearch}
                                    onQuestionBankSearchClear={handleQuestionBankSearchClear}
                                    questionBankCategories={questionBankCategories}
                                    customQuestions={customQuestions}
                                    onEditCustomQuestion={handleEditCustomQuestion}
                                    onDeleteCustomQuestion={handleDeleteCustomQuestion}
                                    isLoading={isLoading}
                                    showSidebarToggleInPanel={disableNavbar}
                                    onSidebarToggleInPanel={disableNavbar ? handleMenuToggle : undefined}
                                    data-test-id={`${dataTestId}-landing-content`}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticMainLayout;
